import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchMovies } from '../api/tmdbApi.js';
import { PAGINATION } from '../constants/config.js';

/**
 * Custom hook for managing movie search functionality
 * Handles search queries, genre filtering, pagination, and search results
 *
 * @param {Object} genresMap - Map of genre IDs to names
 * @param {Function} showSnackbar - Function to show notifications
 * @returns {Object} Search state and management functions
 */
const useMovieSearch = (genresMap, showSnackbar) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Loading and error state
  const [isLoadingMovies, setIsLoadingMovies] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [movieFetchError, setMovieFetchError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(PAGINATION.initialPage);
  const [totalPages, setTotalPages] = useState(1);

  // Refs
  const searchInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Cancel any ongoing requests
   */
  const cancelPendingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cancel requests on unmount
  useEffect(() => {
    return () => {
      cancelPendingRequests();
    };
  }, [cancelPendingRequests]);

  /**
   * Internal function to fetch movies with new API
   */
  const fetchMoviesInternal = useCallback(async (query, genreId, page, append = false) => {
    // Cancel any pending requests
    cancelPendingRequests();

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const { data, error } = await fetchMovies({
      query,
      genreId,
      page,
      genresMap,
      signal: abortControllerRef.current.signal,
    });

    // Clear abort controller after request completes
    abortControllerRef.current = null;

    if (error) {
      setMovieFetchError(error);
      if (error !== 'Request cancelled') {
        showSnackbar(error, 'error');
      }
      return false;
    }

    // Update results
    if (append) {
      setSearchResults(prev => [...prev, ...data.movies]);
    } else {
      setSearchResults(data.movies);
    }

    setCurrentPage(data.page);
    setTotalPages(data.totalPages);
    setMovieFetchError('');

    return true;
  }, [genresMap, showSnackbar, cancelPendingRequests]);

  /**
   * Handle search query input change
   * @param {Event|string} queryOrEvent - The search query string or event object
   */
  const handleSearchChange = useCallback((queryOrEvent) => {
    const query = typeof queryOrEvent === 'string'
      ? queryOrEvent
      : queryOrEvent.target.value;
    setSearchQuery(query);
  }, []);

  /**
   * Handle genre filter change
   * @param {string} genreId - The genre ID to filter by
   */
  const handleGenreChange = useCallback(async (genreId) => {
    setSelectedGenre(genreId);
    setSearchQuery('');
    setActiveSearchTerm('');
    setCurrentPage(PAGINATION.initialPage);

    // Fetch movies for the selected genre
    if (genreId) {
      setIsLoadingMovies(true);
      await fetchMoviesInternal('', genreId, PAGINATION.initialPage, false);
      setIsLoadingMovies(false);
    }
  }, [fetchMoviesInternal]);

  /**
   * Fetch initial popular movies (no search query)
   */
  const fetchInitialMovies = useCallback(async () => {
    setCurrentPage(PAGINATION.initialPage);
    setIsLoadingMovies(true);
    setMovieFetchError('');

    await fetchMoviesInternal('', '', PAGINATION.initialPage, false);

    setIsLoadingMovies(false);
  }, [fetchMoviesInternal]);

  /**
   * Handle search form submission
   * @param {Event} e - The form submit event
   */
  const handleSearchSubmit = useCallback(async (e) => {
    e?.preventDefault();

    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery && !selectedGenre) {
      return;
    }

    setActiveSearchTerm(trimmedQuery);
    setCurrentPage(PAGINATION.initialPage);
    setIsLoadingMovies(true);
    setMovieFetchError('');

    await fetchMoviesInternal(
      trimmedQuery,
      selectedGenre,
      PAGINATION.initialPage,
      false
    );

    setIsLoadingMovies(false);
  }, [searchQuery, selectedGenre, fetchMoviesInternal]);

  /**
   * Load more search results (pagination)
   */
  const handleLoadMore = useCallback(async () => {
    if (currentPage >= totalPages || isLoadingMovies || isLoadingMore) {
      return;
    }

    const nextPage = currentPage + 1;
    setIsLoadingMore(true);

    await fetchMoviesInternal(
      activeSearchTerm,
      selectedGenre,
      nextPage,
      true // append results
    );

    // Delay clearing loading state to allow cards to render
    setTimeout(() => {
      setIsLoadingMore(false);
    }, 300);
  }, [
    currentPage,
    totalPages,
    isLoadingMovies,
    isLoadingMore,
    activeSearchTerm,
    selectedGenre,
    fetchMoviesInternal,
  ]);

  /**
   * Clear search results and reset state
   */
  const clearSearch = useCallback(() => {
    cancelPendingRequests();
    setSearchQuery('');
    setActiveSearchTerm('');
    setSelectedGenre('');
    setSearchResults([]);
    setCurrentPage(PAGINATION.initialPage);
    setTotalPages(1);
    setMovieFetchError('');
    setIsLoadingMovies(false);
    setIsLoadingMore(false);
  }, [cancelPendingRequests]);

  /**
   * Focus the search input
   */
  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  /**
   * Clear search error
   */
  const clearSearchError = useCallback(() => {
    setMovieFetchError('');
  }, []);

  /**
   * Check if there are more pages to load
   */
  const hasMorePages = currentPage < totalPages;

  /**
   * Check if currently searching
   */
  const isSearching = Boolean(activeSearchTerm || selectedGenre);

  return {
    // Search state
    searchQuery,
    activeSearchTerm,
    selectedGenre,
    searchResults,

    // Loading and error state
    isLoadingMovies,
    isLoadingMore,
    movieFetchError,

    // Pagination state
    currentPage,
    totalPages,
    hasMorePages,

    // Refs
    searchInputRef,

    // Computed state
    isSearching,

    // Actions
    fetchInitialMovies,
    handleSearchChange,
    handleGenreChange,
    handleSearchSubmit,
    handleLoadMore,
    clearSearch,
    focusSearchInput,
    clearSearchError,
  };
};

export default useMovieSearch;
